import { normalizeTimeValue } from '../../../common/ecxTimeUtils';

export const APPOINTMENT_STATUS_OPTIONS = [
    { value: 'ALL', label: 'All Status' },
    { value: '0', label: 'Not Confirmed' },
    { value: '1', label: 'Confirmed by Eyeconx' },
    { value: '2', label: 'Confirmed by PMS' },
    { value: '3', label: 'Cancelled' },
];

export const APPOINTMENT_STATUS_LABELS = [
    'Not Confirmed',
    'Confirmed by Eyeconx',
    'Confirmed by PMS',
    'Cancelled',
];

export const REMINDER_SENT_TYPES = ['Not Sent', 'Email', 'SMS', 'Call', 'Live Call'];

export const REMINDER_DEFINITIONS = [
    {
        key: 'prebooked',
        label: 'Prebooked',
        shortLabel: 'PB',
        enabledKey: 'isPrebooked',
        statusKey: 'prebookedStatus',
        sentTypeKey: 'prebookedSentType',
        dateKey: 'datePrebooked',
    },
    {
        key: 'first',
        label: 'First',
        shortLabel: '1st',
        enabledKey: 'isFirst',
        statusKey: 'firstStatus',
        sentTypeKey: 'firstSentType',
        dateKey: 'dateFirst',
    },
    {
        key: 'second',
        label: 'Second',
        shortLabel: '2nd',
        enabledKey: 'isSecond',
        statusKey: 'secondStatus',
        sentTypeKey: 'secondSentType',
        dateKey: 'dateSecond',
    },
    {
        key: 'last',
        label: 'Last',
        shortLabel: 'Last',
        enabledKey: 'isLast',
        statusKey: 'lastStatus',
        sentTypeKey: 'lastSentType',
        dateKey: 'dateLast',
    },
    {
        key: 'sameDay',
        label: 'Same Day',
        shortLabel: 'Same',
        enabledKey: 'isSameDay',
        statusKey: 'sameDayStatus',
        sentTypeKey: 'sameDaySentType',
        dateKey: 'dateSameDay',
    },
];

export const getReminderSentTypeLabel = (sentType) =>
    REMINDER_SENT_TYPES[Number(sentType)] || 'Unknown';

export const getReminderIcon = (sentType) => {
    switch (Number(sentType)) {
        case 1:
            return { icon: 'bi-envelope-fill', variant: 'email', colorClass: 'primary' };
        case 2:
            return { icon: 'bi-chat-dots-fill', variant: 'sms', colorClass: 'info' };
        case 3:
            return { icon: 'bi-telephone-fill', variant: 'call', colorClass: 'success' };
        case 4:
            return { icon: 'bi-check-circle-fill', variant: 'live-call', colorClass: 'success' };
        default:
            return { icon: 'bi-dash-circle', variant: 'none', colorClass: 'secondary' };
    }
};

export const getReminderItems = (status = {}) =>
    REMINDER_DEFINITIONS.map((definition) => {
        const enabled = Boolean(status[definition.enabledKey]);
        const responseStatus = Boolean(status[definition.statusKey]);
        const sentType = Number(status[definition.sentTypeKey] || 0);
        const date = status[definition.dateKey] || null;
        const isSent = sentType > 0 || Boolean(date);

        return {
            ...definition,
            enabled,
            responseStatus,
            sentType,
            date,
            isSent,
            sentTypeLabel: getReminderSentTypeLabel(sentType),
            channelIcon: getReminderIcon(sentType),
            active: enabled,
            type: sentType,
        };
    });

export const getReminderPillClassName = (reminder) => {
    if (!reminder.enabled) {
        return 'appointments-reminder-pill--inactive';
    }

    if (reminder.isSent) {
        return reminder.responseStatus
            ? 'appointments-reminder-pill--sent appointments-reminder-pill--responded'
            : 'appointments-reminder-pill--sent';
    }

    return 'appointments-reminder-pill--pending';
};

export const getReminderTooltipText = (reminder) => {
    if (!reminder.isSent) {
        return `${reminder.label} reminder has not been sent yet.`;
    }

    const channel =
        reminder.sentTypeLabel && reminder.sentTypeLabel !== 'Not Sent'
            ? reminder.sentTypeLabel.toLowerCase()
            : 'unknown channel';

    if (reminder.date) {
        return `${reminder.label} reminder was sent via ${channel} on ${reminder.date}.`;
    }

    return `${reminder.label} reminder was sent via ${channel}.`;
};

export const formatAppointmentDate = (date) => {
    if (!date) {
        return '';
    }

    const value = date instanceof Date ? date : new Date(date);
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

export const formatAppointmentCount = (count) => Number(count || 0).toLocaleString();

export const getAppointmentTitle = (date) => {
    const selected = date instanceof Date ? date : new Date(date);
    const today = new Date();

    if (
        today.getDate() === selected.getDate() &&
        today.getMonth() === selected.getMonth() &&
        today.getFullYear() === selected.getFullYear()
    ) {
        return "Today's Appointments";
    }

    return `Appointments on ${selected.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })}`;
};

export const isAppointmentConfirmed = (appointment) =>
    appointment?.isConfirmed === 1 || appointment?.isConfirmed === 2;

export const getAppointmentStatusMeta = (status) => {
    switch (Number(status)) {
        case 1:
            return { label: APPOINTMENT_STATUS_LABELS[1], variant: 'success', icon: 'bi-check-circle-fill' };
        case 2:
            return { label: APPOINTMENT_STATUS_LABELS[2], variant: 'primary', icon: 'bi-check-circle' };
        case 3:
            return { label: APPOINTMENT_STATUS_LABELS[3], variant: 'danger', icon: 'bi-x-circle-fill' };
        default:
            return { label: APPOINTMENT_STATUS_LABELS[0], variant: 'secondary', icon: 'bi-clock' };
    }
};

export const getPatientInitials = (patient) => {
    const first = patient?.firstName?.[0] || '';
    const last = patient?.lastName?.[0] || '';
    const initials = `${first}${last}`.trim().toUpperCase();

    return initials || null;
};

const UNPARSEABLE_APPOINTMENT_TIME = Number.MAX_SAFE_INTEGER;

export const parseAppointmentTimeToMinutes = (timeValue) => {
    if (!timeValue) {
        return UNPARSEABLE_APPOINTMENT_TIME;
    }

    const value = String(timeValue).trim();
    const normalized = normalizeTimeValue(value);

    if (/^\d{1,2}:\d{2}$/.test(normalized)) {
        const [hours, minutes] = normalized.split(':').map(Number);

        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
            return hours * 60 + minutes;
        }
    }

    const match12Hour = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);

    if (match12Hour) {
        let hours = Number(match12Hour[1]);
        const minutes = Number(match12Hour[2]);
        const period = match12Hour[3].toUpperCase();

        if (period === 'PM' && hours !== 12) {
            hours += 12;
        }

        if (period === 'AM' && hours === 12) {
            hours = 0;
        }

        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
            return hours * 60 + minutes;
        }
    }

    const parsed = Date.parse(`1970-01-01 ${value}`);

    if (!Number.isNaN(parsed)) {
        const date = new Date(parsed);

        return date.getHours() * 60 + date.getMinutes();
    }

    return UNPARSEABLE_APPOINTMENT_TIME;
};

export const sortAppointmentsByTime = (appointments = []) =>
    [...appointments].sort((left, right) => {
        const timeDiff =
            parseAppointmentTimeToMinutes(left.startTime) - parseAppointmentTimeToMinutes(right.startTime);

        if (timeDiff !== 0) {
            return timeDiff;
        }

        const leftName = `${left.patient?.lastName || ''}${left.patient?.firstName || ''}`.toLowerCase();
        const rightName = `${right.patient?.lastName || ''}${right.patient?.firstName || ''}`.toLowerCase();

        return leftName.localeCompare(rightName);
    });
